import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import CustomCheckboxDropdown from '../../components/forms/checkBoxDropDown';
import successToast from '../../components/toasts/successToast';
import { useDispatch } from 'react-redux';
import errorToast from '../../components/toasts/errorToast';
import { addTeamAction, teamEditAction, teamViewAction, tlDropDownAction, usersDropDownAction } from '../../redux/actions/teamAction';
import { Spinner } from 'react-bootstrap';
import { IoArrowBack } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';

const AddTeam = () => {
    const { isAdmin } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [teamLeadOptions, setTeamLeadOptions] = useState([]);
    const [teamData, setTeamData] = useState({});
    const currentPath = window.location.pathname;
    const segments = currentPath.split("/");
    const pathSegment = segments[1];
    const { id } = useParams();
    const [disable, setDisable] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const onViewSuccess = (data) => {
        setTeamData(data.data.data);
        setLoading(false);
    };

    const onViewError = (data) => {
        errorToast(data.data.message);
        setLoading(false);
    };

    useEffect(() => {
        if (id) {
            setLoading(true);
            dispatch(teamViewAction({ id }, onViewSuccess, onViewError));
        }
        if (id && pathSegment === "view-team") {
            setDisable(true);
        }
    }, [id, dispatch]);

    const initialValues = {
        id: teamData?.id || "",
        name: teamData?.name || '',
        team_lead: teamData?.team_lead_id || '',
        team_users: teamData?.teamUsers?.map(user => user.user_id) || [],
        description: teamData?.description || ""
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s]+$/, "Name must contain only alphabets").required('Required'),
        team_lead: Yup.string().required('Required'),
        team_users: Yup.array().min(1, 'At least one team member is required'),
    });

    const onSubmit = (values) => {
        if (pathSegment === "edit-team") {
            dispatch(teamEditAction(values, onSuccess, onError));
        } else {
            dispatch(addTeamAction(values, onSuccess, onError));
        }
    };

    useEffect(() => {
        dispatch(usersDropDownAction({ type: pathSegment, id: id }, onUsersSuccess, onError));
        dispatch(tlDropDownAction({ type: pathSegment, id: id }, onTlSuccess, onError));
    }, [])

    const onUsersSuccess = (data) => {
        const usersData = data.data.data.map(user => ({
            label: user.email,
            value: user.id
        }));
        setUserOptions(usersData);
    };

    const onTlSuccess = (data) => {
        const usersData = data.data.data.map(user => ({
            label: user.email,
            value: user.id
        }));
        setTeamLeadOptions(usersData);
    };

    const onError = (data) => {
        errorToast(data?.data?.message);
    };

    const onSuccess = (data) => {
        successToast(data?.data?.message);
        navigate(-1);
    }

    const pathLabels = {
        "edit-team": "Edit Team",
        "view-team": "View Team",
        "add-team": "Add Team",
    };

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setDropdownOpen(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <i className="bi bi-shield-lock fs_40 text-danger mb-3"></i>
                <h5 className="fw_600">Access Denied</h5>
                <p className="text-muted fs_14">This page is only accessible to company administrators.</p>
            </div>
        );
    }

    return (
        <div className="d-flex vh-100" onClick={handleClickOutside}>
            <div className="w-100 ">
                <div className="w-100 d-flex align-items-center header border-bottom position-sticky top-0 bg-white py-2">
                    <span className="px-1 fs_16 cursor_pointer pb-1" onClick={() => navigate(-1)}><IoArrowBack /></span>
                    <h2 className="fs_20 fw_400 fn_Roboto text-start px-3 pt-1">{pathLabels[pathSegment]}</h2>
                </div>
                {loading ?
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "90%" }}>
                        <Spinner animation="border" variant="primary" /> <span>Loading...</span>
                    </div>
                    :
                    <div className="flex-grow-1 pt-3">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                            enableReinitialize
                        >
                            {({ values, setFieldValue, isValid, dirty }) => (
                                <Form className="d-flex flex-column h-100 justify-content-between">
                                    <div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="name" className="py-1">Team Name</label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    type="text"
                                                    placeholder="Enter Team Name"
                                                    id="name"
                                                    name="name"
                                                    className="input-field"
                                                    disabled={disable}
                                                    style={{ width: '20rem' }}
                                                    onInput={(e) => {
                                                        e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                                    }}
                                                />
                                            </div>
                                            <ErrorMessage name="name" component="div" className="text-danger mt-1 fs_12" />
                                        </div>

                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4" ref={dropdownRef}>
                                            <label htmlFor="team_users" className="py-1">Team Users</label>
                                            <CustomCheckboxDropdown
                                                options={userOptions}
                                                name="team_users"
                                                value={values.team_users}
                                                disabled={disable}
                                                onChange={setFieldValue}
                                                placeholder={'Select Team Users'}
                                                dropdownOpen={dropdownOpen}
                                                setDropdownOpen={setDropdownOpen}
                                            />
                                            <ErrorMessage name="team_users" component="div" className="text-danger mt-1 fs_12" />
                                        </div>


                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="team_lead" className="py-1">Team Lead</label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    as="select"
                                                    id="team_lead"
                                                    name="team_lead"
                                                    disabled={disable}
                                                    className="input-field"
                                                    style={{ width: '20rem' }}
                                                >
                                                    <option value="">Select Lead</option>
                                                    {teamLeadOptions.map((user) => (
                                                        <option key={user.value} value={user.value}>
                                                            {user.label}
                                                        </option>
                                                    ))}
                                                </Field>
                                            </div>
                                            <ErrorMessage name="team_lead" component="div" className="text-danger mt-1 fs_12" />
                                        </div>



                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor={`description`} className="py-1">Description</label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    name={`description`}
                                                    as="textarea"
                                                    className="input-field"
                                                    disabled={disable}
                                                    id={`description`}
                                                    style={{ width: '20rem' }}
                                                />
                                            </div>
                                            <ErrorMessage name={`description`} component="div" className="text-danger mt-1 fs_12" />
                                        </div>
                                    </div>
                                    {/* {loading && !disable && (
                                        <div className="d-flex align-items-center text-center">
                                            <Spinner size="sm" className="text-primary" />{" "}
                                            <span className="ms-2">Updating...</span>
                                        </div>
                                    )} */}
                                    {!disable && <div className="d-flex gap-5 mt-4 mx-4 my-3">
                                        <button type="submit" className="btn btn-primary" style={{ width: '5rem' }} disabled={!(isValid && dirty)} >{pathSegment === "edit-team" ? "Update" : "Create"}</button>
                                        <button type="button" className="btn btn-secondary" style={{ width: '5rem' }} onClick={() => navigate(-1)}>Cancel</button>
                                    </div>}
                                </Form>
                            )}
                        </Formik>
                    </div>
                }
            </div>
        </div>
    );
};

export default AddTeam;

